import { z } from 'zod';
import type { Tenant } from './schemas';

/**
 * Contrato da busca server-side da lista de Empresas no admin
 * (callable `searchTenants`). PropertyFilter do Cloudscape → tokens
 * normalizados aqui → queries Firestore na Function.
 *
 * Restrições DURAS do Firestore (issue #71, Opção B):
 * - sem `contains`/substring: texto só faz `=` (exato) ou `:` (prefixo, via
 *   range `>= termo` + `< termo+`);
 * - só UM campo de range/desigualdade por query.
 *
 * Para limitar a explosão de índices compostos, a Function classifica os
 * tokens em dois grupos:
 * - **igualdade** (combináveis, ordenados por `createdAt desc`): `status`
 *   (`=`), `kind` (`=`), `country` (`=`) — e `createdAt` (`>=`/`<=`), que
 *   coincide com a ordenação;
 * - **standalone** (token ÚNICO): `name`/`id`/`taxId` (`=`/`:`),
 *   `statusChangedAt` (`>=`/`<=`) e `status !=`.
 *
 * Combinação fora dessas regras → `invalid-argument`.
 */
export const TENANT_SEARCH_PROPERTY_KEYS = [
  'name',
  'id',
  'taxId',
  'status',
  'kind',
  'country',
  'createdAt',
  'statusChangedAt',
] as const;
export const TenantSearchPropertyKeySchema = z.enum(TENANT_SEARCH_PROPERTY_KEYS);
export type TenantSearchPropertyKey = z.infer<typeof TenantSearchPropertyKeySchema>;

/** Operadores expostos pelo PropertyFilter (subconjunto suportado). */
export const TENANT_SEARCH_OPERATORS = ['=', '!=', ':', '>=', '<='] as const;
export const TenantSearchOperatorSchema = z.enum(TENANT_SEARCH_OPERATORS);
export type TenantSearchOperator = z.infer<typeof TenantSearchOperatorSchema>;

export const TenantSearchTokenSchema = z.object({
  propertyKey: TenantSearchPropertyKeySchema,
  operator: TenantSearchOperatorSchema,
  /** Valor sempre como string; datas chegam ISO (`YYYY-MM-DD`) e a Function parseia. */
  value: z.string().min(1).max(120),
});
export type TenantSearchToken = z.infer<typeof TenantSearchTokenSchema>;

export const SearchTenantsInputSchema = z.object({
  tokens: z.array(TenantSearchTokenSchema).max(8).default([]),
  /** Firestore não faz OR entre campos diferentes; só `and` é suportado. */
  operation: z.literal('and').default('and'),
  page: z.number().int().min(1).max(1000).default(1),
  pageSize: z.number().int().min(1).max(100).default(25),
});
export type SearchTenantsInput = z.infer<typeof SearchTenantsInputSchema>;

/**
 * Resultado paginado (offset + count). `total` é a contagem do conjunto
 * filtrado (sem paginar) via aggregation `count()` do Firestore.
 */
export type SearchTenantsResult = {
  items: Tenant[];
  total: number;
  page: number;
  pageSize: number;
};
